import { Box, Button, useTheme, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useEffect, useState } from "react";
import { db, collection, getDocs } from "../../firebase";

const Requests = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState(""); // Status filter state

  useEffect(() => {
    const fetchData = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "order"));
        const userData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRows(userData);
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };
    fetchData();
  }, []);

  const exportToCSV = () => {
    const csvRows = [];
    const headers = ["Name", "Phone Number", "Address", "Date", "Status", "Time Taken", "Distance"];
    csvRows.push(headers.join(",")); // Add headers

    rows.forEach((row) => {
      const startTime = row.start_time ? new Date(row.start_time.seconds * 1000) : null;
      const endTime = row.end_time ? new Date(row.end_time.seconds * 1000) : null;
      const timeTaken = startTime && endTime ? ((endTime - startTime) / 1000 / 60).toFixed(2) + " mins" : "N/A";

      const values = [
        row.name,
        row.phone,
        row.address,
        row.createdAt?.seconds ? new Date(row.createdAt.seconds * 1000).toLocaleDateString("en-GB") : "N/A",
        row.status,
        timeTaken,
        row.distance
      ];
      csvRows.push(values.join(","));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "requests.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Apply status filter
  const filteredRows = statusFilter ? rows.filter((row) => row.status === statusFilter) : rows;

  const columns = [
    { field: "name", headerName: "Name", flex: 1, cellClassName: "name-column--cell" },
    { field: "phone", headerName: "Phone Number", flex: 1 },
    { field: "address", headerName: "Address", flex: 1 },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 1,
      renderCell: ({ value }) =>
        value?.seconds
          ? new Date(value.seconds * 1000)
              .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
              .replace(",", "")
          : "N/A",
    },
    { field: "status", headerName: "Status", flex: 1 },
    {
      field: "timeTaken",
      headerName: "Time Taken",
      flex: 1,
      valueGetter: (params) => {
        const startTime = params.row.start_time ? new Date(params.row.start_time.seconds * 1000) : null;
        const endTime = params.row.end_time ? new Date(params.row.end_time.seconds * 1000) : null;
        return startTime && endTime ? `${((endTime - startTime) / 1000 / 60).toFixed(2)} mins` : "N/A";
      },
    },
    { field: "distance", headerName: "Distance", flex: 1 },
  ];

  return (
    <Box m="20px">
      <Header title="REQUEST MANAGEMENT" subtitle="List of Requests" />

      {/* Status Filter */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PENDING">PENDING</MenuItem>
            <MenuItem value="ENDED">ENDED</MenuItem>
            <MenuItem value="CANCELLED">CANCELLED</MenuItem>
            <MenuItem value="ONGOING">ONGOING</MenuItem>
          </Select>
        </FormControl>

        <Button variant="contained" color="primary" onClick={exportToCSV}>
          Download CSV
        </Button>
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none", fontSize: "14px" },
          "& .MuiDataGrid-cell": { borderBottom: "none", fontSize: "14px" },
          "& .name-column--cell": { color: colors.greenAccent[300], fontWeight: "bold" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            color: "#fff",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
            color: "#fff",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-row:hover": { backgroundColor: colors.primary[600] },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
        }}
      >
        <DataGrid
          checkboxSelection
          rows={filteredRows} // Apply filtered rows
          columns={columns}
          getRowHeight={() => 55}
        />
      </Box>
    </Box>
  );
};

export default Requests;
